import type { AuditsParams, AuditsQueryInput } from '../-types';

/**
 * The columns the admin list maps into its `ORDER BY`.
 *
 * Every key in the route's `SORT_COLUMNS_ENUM` has a real mapping in
 * `SORT_COLUMNS_MAPPING_PG`, so unlike the documents list nothing here is a
 * phantom. This is the subset the page actually exposes a header for.
 *
 * `id` is **not** among them. It is filterable but absent from the sort enum,
 * and `parseSortingPg` drops an unmapped key and falls back to
 * `updated_at DESC` — so the old dashboard's ID header silently reordered by
 * last-updated instead. The column ships without a sort rather than with one
 * that lies.
 */
const SORTABLE_FIELDS = new Set([
  'name',
  'type',
  'referenceNumber',
  'companyName',
  'uploaderCompanyName',
  'facilityScore',
  'auditedBy',
  'auditDate',
  'nextPlannedAuditDate',
  'auditReportFileId',
  'updatedAt',
]);

/**
 * The columns the admin list filters on.
 *
 * All mapped, all against tables the query joins.
 *
 * `type` is `useLike` **without** `arrayable`, so `buildFilterPg` turns a
 * comma-joined value into one `LIKE %a,b%` that matches nothing — the filter is
 * single-choice for that reason, as on the documents list.
 */
const FILTERABLE_FIELDS = new Set([
  'id',
  'name',
  'type',
  'referenceNumber',
  'companyName',
  'uploaderCompanyName',
  'facilityScore',
  'auditedBy',
  'auditReportFileId',
]);

/**
 * Turns table state into the endpoint's parameters.
 *
 * `page` converts from the UI's 1-based count to the endpoint's 0-based one —
 * `readPagination` offsets by `page * count`.
 *
 * There is no `q`. The route's querystring accepts it and `listAdmin` never
 * reads it, so a search box would look live and filter nothing.
 */
export function toAuditsParams(input: AuditsQueryInput): AuditsParams {
  const filter: Record<string, string> = {};
  for (const [key, values] of Object.entries(input.filters ?? {})) {
    const value = values?.[0];
    if (FILTERABLE_FIELDS.has(key) && value) filter[key] = value;
  }

  const [field] = (input.sort ?? '').split(':');
  const sort = field && SORTABLE_FIELDS.has(field) ? input.sort : undefined;

  return {
    page: Math.max(0, input.page - 1),
    count: input.limit,
    ...(sort ? { sort } : {}),
    ...(Object.keys(filter).length > 0
      ? { filter: JSON.stringify(filter) }
      : {}),
  };
}

export function buildAuditsQs(params: AuditsParams): string {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('count', String(params.count));
  if (params.sort) qs.set('sort', params.sort);
  if (params.filter) qs.set('filter', params.filter);
  return qs.toString();
}
